import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./QuestionTypes.module.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { Grid } from "@mui/material";
import { useDispatch } from "react-redux";
import { handleErrorMessage } from "../../../../../redux/actions/groups";
import { validateQuestion } from "../../../../../utils";
import { useTranslation } from "react-i18next";

const GridQuestionComponent = forwardRef(
  ({ onSave, onCancel, onMoveUp, onMoveDonw, onDeleteQuestion, initialData = {} }, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Local state
    const [question, setQuestion] = useState("");
    const [rows, setRows] = useState([""]);
    const [columns, setColumns] = useState([""]);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const [maxMarks, setMaxMarks] = useState("10");
    const [isRequired, setIsRequired] = useState(false);

    const containerRef = useRef(null);

    // keep click-outside behaviour (for focusedIndex)
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setFocusedIndex(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    // Helper to preserve incoming choice IDs or generate stable ones
    function generateChoiceId(baseId, index) {
      const src = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];
      if (src[index] && src[index].id) return src[index].id;
      return `grid-${initialData.id ?? Date.now()}-${index}`;
    }

    // Initialize from initialData only once
    const initializedRef = useRef(false);
    useEffect(() => {
      if (initializedRef.current || !initialData) return;
      initializedRef.current = true;

      setQuestion(
        initialData.questionText ??
         initialData.text ??
          initialData.question ??
          ""
      );

      setMaxMarks(
        initialData.maxMarks ??
          initialData.marks ??
          initialData.max_marks ??
          (typeof initialData.marks === "number" ? String(initialData.marks) : "") ??
          "10"
     );

      setIsRequired(initialData.isMandatory ?? initialData.is_mandatory ?? false);

      const srcChoices = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];

      if (Array.isArray(srcChoices) && srcChoices.length) {
        const normalizedRows = srcChoices.map(
          (c) => c.choiceText ?? c.choice_text ?? c.text ?? ""
        );
        const normalizedCols = srcChoices.map(
          (c) => c.correctChoice ?? c.correct_choice ?? c.correct_choice_text ?? ""
        );

        setRows(normalizedRows.length ? normalizedRows : [""]);
        setColumns(normalizedCols.length ? normalizedCols : normalizedRows.map(() => ""));
      } else {
        setRows([""]);
        setColumns([""]);
      }
    }, [initialData]);

    // Add a new row+column pair
    const addRowColumn = () => {
      setRows((r) => [...r, ""]);
      setColumns((c) => [...c, ""]);
      setFocusedIndex(rows.length); // focus newly added row
    };

    // Remove a row+column pair at index
    const removeRowColumn = (index) => {
      if (rows.length <= 1 && columns.length <= 1) return;
      const updatedRows = [...rows];
      const updatedCols = [...columns];
      updatedRows.splice(index, 1);
      updatedCols.splice(index, 1);
      setRows(updatedRows.length ? updatedRows : [""]);
      setColumns(updatedCols.length ? updatedCols : [""]);
    };

    const handleRowChange = (val, idx) => {
      const updated = [...rows];
      updated[idx] = val;
      setRows(updated);
    };

    const handleColumnChange = (val, idx) => {
      const updated = [...columns];
      updated[idx] = val;
      setColumns(updated);
    };

    // Expose save() to parent
    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    function handleSave() {
      // Basic validation using existing validator (type "grid")
      const errorMessage = validateQuestion({
        question,
        marks: maxMarks,
        options: rows,
        correctAnswer: columns,
        type: "grid",
      });

      if (errorMessage) {
        dispatch(handleErrorMessage(errorMessage));
        return null;
      }

      // Normalize payload to match parent expectations
      const formattedChoices = rows.map((row, index) => ({
        id: generateChoiceId(initialData.id, index),
        choiceText: row || `${index + 1}`,
        correctChoice: columns[index] ?? "",
      }));

      const payload = {
        id: initialData.id ?? `q-${Date.now()}`,
        type: "grid",
        questionText: question?.trim() ?? "",
        choices: formattedChoices,
        isMandatory: !!isRequired,
        maxMarks: parseInt(maxMarks, 10) || 0,
        icon: initialData.icon ?? null,
      };

      // return payload (parent expects save() to return it)
      return payload;
    }

    return (
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <div className={styles.questionType}>
            <div className={styles.radioIcon}>
              <div className={styles.radioInner}></div>
            </div>
            <span>{t("quiz.grid")}</span>
          </div>

          <div className={styles.questionActions}>
            <button className={styles.actionButton} onClick={onMoveUp} type="button">
              <KeyboardArrowUpIcon />
            </button>
            <button className={styles.actionButton} onClick={onMoveDonw} type="button">
              <KeyboardArrowDownIcon />
            </button>
            <button className={styles.actionButton} onClick={onDeleteQuestion} type="button">
              <DeleteOutlineIcon />
            </button>
            <div className={styles.marksContainer}>
              <span>{t("quiz.maxMarks")}</span>
              <input
                min={"0"}
                type="number"
                className={styles.marksInput}
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
              />
            </div>
            <div className={styles.requiredContainer}>
              <span>{t("quiz.required")}</span>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.questionContent}>
          <input
            type="text"
            className={styles.questionInput}
            placeholder={t("quiz.question")}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <Grid sx={{ display: "flex" }}>
            <div className={styles.optionsContainer} ref={containerRef}>
              {rows.map((row, rIdx) => (
                <div
                  key={`row-${rIdx}`}
                  className={styles.optionItem}
                  onClick={() => setFocusedIndex(rIdx)}
                >
                  <span>{rIdx + 1}.</span>
                  <input
                    type="text"
                    className={`${styles.optionInput} `}
                    placeholder={t("PollModal.addOption")}
                    value={row}
                    onChange={(e) => handleRowChange(e.target.value, rIdx)}
                  />
                </div>
              ))}

              <button className={styles.addOptionButton} onClick={addRowColumn} type="button">
                <AddCircleOutlineIcon sx={{ color: "#F33070" }} />
              </button>
            </div>

            <div className={styles.optionsContainer} ref={containerRef}>
              {columns.map((col, cIdx) => (
                <div
                  key={`col-${cIdx}`}
                  className={styles.gridRow}
                  onClick={() => setFocusedIndex(cIdx)}
                >
                  <span>{String.fromCharCode(65 + cIdx)}.</span>
                  <input
                    type="text"
                    className={`${styles.optionInput} `}
                    placeholder="Add Correct Answer"
                    value={col}
                    onChange={(e) => handleColumnChange(e.target.value, cIdx)}
                  />

                  {focusedIndex === cIdx && (
                    <CloseIcon
                      className={styles.removeIcon}
                      onClick={() => removeRowColumn(cIdx)}
                    />
                  )}
                </div>
              ))}
            </div>
          </Grid>
        </div>
      </div>
    );
  }
);

export default GridQuestionComponent;
